import { useCallback, useMemo } from 'react'

import { useValueRef } from '../hook'
import { isUpdaterFn, UpdaterFn, NoInfer, BaseStorageHookOptions } from '../types'
import {
  getValidationSchemeFromConfig,
  StorableType,
  BaseValue,
  validateByScheme,
} from '../validation'
import { createLsParser, CreateParserOptions } from './createLsParser'
import { useLocalStorage, UseLocalStorageOptions } from './useLocalStorage'

type ReducedLocalStorageOptions<TValue> = Omit<
  UseLocalStorageOptions<TValue>,
  'parser' | 'serializer'
>

export type UseStorageStateOptions<
  TType extends StorableType,
  TValue extends BaseValue<TType>,
> = BaseStorageHookOptions<TType, TValue> &
  ReducedLocalStorageOptions<NoInfer<TValue>> &
  CreateParserOptions

export function useLocalStorageState<TType extends StorableType, TValue extends BaseValue<TType>>(
  key: string,
  options: UseStorageStateOptions<TType, TValue>,
) {
  const { schema, storageHookOptions } = useLocalStorageStateOptions(options)

  const [value, _internalSetValue, getCurrentValue] = useLocalStorage(key, storageHookOptions)

  /**
   * Ref необходим, чтобы сделать сеттер-функцию (`setValue`) практически постоянной.
   * Избежать её изменения из-за options или internalValue
   */
  const valueRef = useValueRef(value)
  const optionRef = useValueRef(options)

  const internalSetValue = useCallback(
    (newValue: TValue | null) => {
      _internalSetValue(newValue)
      valueRef.current = newValue
    },
    [_internalSetValue, valueRef],
  )

  const setValue = useCallback(
    (valueOrUpdater: TValue | UpdaterFn<TValue> | null) => {
      const oldValue = valueRef.current
      const options = optionRef.current

      const newValue = isUpdaterFn(valueOrUpdater) ? valueOrUpdater(oldValue) : valueOrUpdater

      if (newValue === null) {
        internalSetValue(null)
        return true
      }

      const { success, data, error } = validateByScheme<TValue>(newValue, schema)

      if (!success) {
        options.onValidationError?.(error)
        return false
      }

      internalSetValue(data)
      return true
    },
    [internalSetValue, optionRef, schema, valueRef],
  )

  return [value, setValue, { getCurrentValue }] as const
}

/**
 * @internal
 *
 * Вспомогательный хук. Подготавливает конфиг для основного
 */
function useLocalStorageStateOptions<TType extends StorableType, TValue extends BaseValue<TType>>(
  options: UseStorageStateOptions<TType, TValue>,
) {
  const { defaultValue = null, validation, type, onValidationError } = options

  const schema = useMemo(() => {
    return getValidationSchemeFromConfig(type, validation)
  }, [type, validation])

  const parser = useMemo(() => {
    return createLsParser<TValue>(schema, { onValidationError })
  }, [onValidationError, schema])

  const storageHookOptions = useMemo((): UseLocalStorageOptions<TValue> => {
    return {
      ...options,

      parser,
      defaultValue,
    }
  }, [defaultValue, options, parser])

  return {
    storageHookOptions,
    schema,
  }
}
